import { useState, useCallback } from 'react';
import useToken from '../../../hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';
/**
 * Hook personalizado para manejar la edición de precios y cantidades de productos
 */
const useProductosEdicion = (
    ordenDetalles,
    orden,
    setOrden,
    actualizarMonedaLinksPagos,
    ObtenerDetallesOrden,
    GuardarComentario,
    onComentarioAgregado
) => {
    const { token, setToken, nombreCompleto } = useToken();
    const { showErrorToast, showSuccessToast } = useToast();
    const [showEditarPrecios, setShowEditarPrecios] = useState(false);
    const [editedProducts, setEditedProducts] = useState({});
    const [editedMoneda, setEditedMoneda] = useState('');
    const [guardandoCambios, setGuardandoCambios] = useState(false);

    /**
     * Inicializa los valores editables cuando entra en modo edición
     */
    const inicializarEdicion = useCallback(() => {
        const productosEditables = {};
        ordenDetalles.forEach(product => {
            // Calcular el precio unitario CON impuestos (precio final que ve el cliente)
            const precioUnitarioConImpuestos = parseFloat((product.dImporte + product.dIVA) / product.dCantidad).toFixed(2);

            productosEditables[product.idOrdenDetalle] = {
                dCantidad: product.dCantidad,
                dPrecioUnitario: precioUnitarioConImpuestos, // Precio CON impuestos
                dDescuento: product.dDescuento,
                dImporte: product.dImporte, // Importe SIN impuestos
                dIVA: product.dIVA
            };
        });
        setEditedProducts(productosEditables);
        setEditedMoneda(orden.vMoneda);
        setShowEditarPrecios(true);
    }, [ordenDetalles, orden.vMoneda]);

    /**
     * Maneja cambios en los campos editables
     */
    const handleProductChange = useCallback((idOrdenDetalle, campo, valor) => {
        setEditedProducts(prev => {
            const updated = { ...prev };
            const product = { ...updated[idOrdenDetalle] };

            // Actualizar el campo específico
            product[campo] = parseFloat(valor) || 0;

            // Recalcular automáticamente cuando cambie cantidad o precio unitario
            if (campo === 'dCantidad' || campo === 'dPrecioUnitario') {
                const cantidad = campo === 'dCantidad' ? product[campo] : product.dCantidad;
                const precioUnitarioConImpuestos = campo === 'dPrecioUnitario' ? product[campo] : product.dPrecioUnitario;

                // Calcular precio sin impuestos (precio base)
                const precioUnitarioSinImpuestos = precioUnitarioConImpuestos / 1.16; // Dividir entre 1.16 para quitar el IVA

                // Calcular importe (cantidad × precio sin impuestos) - descuento
                const importeBase = cantidad * precioUnitarioSinImpuestos;
                const importe = importeBase - product.dDescuento;

                // Calcular IVA sobre el importe (sin descuento)
                const iva = importe * 0.16; // 16% IVA

                product.dImporte = importe;
                product.dIVA = iva;
            }

            updated[idOrdenDetalle] = product;
            return updated;
        });
    }, []);

    /**
     * Cancela la edición y restaura valores originales
     */
    const cancelarEdicion = useCallback(() => {
        setEditedProducts({});
        setEditedMoneda('');
        setShowEditarPrecios(false);
    }, []);

    /**
     * Obtiene el tipo de cambio actual de BANXICO para USD
     */
    const obtenerTipoCambioActual = useCallback(async () => {
        try {
            const date = new Date(orden.dFechaVenta).toLocaleString('sv', { timeZoneName: 'short' }).substring(0, 10);
            const response = await fetch(
                `https://www.banxico.org.mx/SieAPIRest/service/v1/series/SF60653/datos/${date}/${date}?decimales=sinCeros&token=7d0140dc6159dbe4a870c61dce8136190da8e9757776cb679483bdaaa31643e6`,
                { method: 'GET' }
            );

            if (response.ok) {
                const data = await response.json();
                return parseFloat(data.bmx.series[0].datos[0].dato);
            } else {
                return 19.555; // Valor de fallback
            }
        } catch (error) {
            return 19.555; // Valor de fallback
        }
    }, [orden.dFechaVenta]);

    /**
     * Actualiza la moneda, tipo de cambio y totales de la orden
     */
    const actualizarMonedaYTotales = useCallback(async (nuevaMoneda, productosModificados = null) => {
        try {
            // 1. Obtener tipo de cambio según la moneda seleccionada
            let tipoCambio = 1.0000;
            if (nuevaMoneda === 'USD') {
                tipoCambio = await obtenerTipoCambioActual();
            }

            // 2. Preparar productos modificados
            let productosParaEnviar = productosModificados;
            if (!productosParaEnviar) {
                productosParaEnviar = Object.keys(editedProducts).length > 0
                    ? Object.keys(editedProducts).map(idOrdenDetalle => {
                        const producto = editedProducts[idOrdenDetalle];
                        return {
                            idOrdenDetalle: parseInt(idOrdenDetalle),
                            dCantidad: producto.dCantidad,
                            dPrecioUnitario: parseFloat(producto.dPrecioUnitario),
                            dDescuento: producto.dDescuento,
                            dImporte: producto.dImporte,
                            dIVA: producto.dIVA,
                            dTotal: producto.dImporte + producto.dIVA
                        };
                    })
                    : ordenDetalles.map(product => ({
                        idOrdenDetalle: product.idOrdenDetalle,
                        dCantidad: product.dCantidad,
                        dPrecioUnitario: parseFloat((product.dImporte - product.dDescuento + product.dIVA) / product.dCantidad),
                        dDescuento: product.dDescuento,
                        dImporte: product.dImporte,
                        dIVA: product.dIVA,
                        dTotal: product.dTotal
                    }));
            }

            // 3. Preparar datos para enviar al backend
            const requestData = {
                idOrden: orden.idOrden,
                vMoneda: nuevaMoneda,
                dTipoCambio: tipoCambio,
                productosModificados: productosParaEnviar
            };

            // 4. Enviar al backend
            const response = await fetch("/api/orden/ActualizarPreciosYCantidades", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            if (response.ok) {
                const data = await response.json();

                if (data.iResponseType === 1) {
                    // 5. Actualizar estado local de la orden
                    setOrden(prevOrden => ({
                        ...prevOrden,
                        vMoneda: nuevaMoneda,
                        dTipoCambio: tipoCambio
                    }));

                    // 6. Actualizar moneda de links de pago si es necesario
                    if (actualizarMonedaLinksPagos && nuevaMoneda !== orden.vMoneda) {
                        actualizarMonedaLinksPagos(nuevaMoneda);
                    }

                    // 7. Recargar datos de la orden para obtener totales actualizados
                    ObtenerDetallesOrden();

                    return { success: true, tipoCambio };
                } else {
                    throw new Error(data.vMessage);
                }
            } else {
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
                throw new Error(`Error ${response.status}: ${response.statusText}`);
            }
        } catch (error) {
            console.error('Error al actualizar moneda y totales:', error);

            // Mostrar alerta de fallback para tipo de cambio si es error de BANXICO
            if (nuevaMoneda === 'USD' && error.message.includes('banxico')) {
                alert("Hubo un problema con la conexión al Banco de México, por lo que estableceremos el Tipo de Cambio a 19.555.");
                // Reintentar con tipo de cambio de fallback
                return await actualizarMonedaYTotales(nuevaMoneda, productosModificados);
            }

            throw error;
        }
    }, [token, setToken, orden, editedProducts, ordenDetalles, setOrden, actualizarMonedaLinksPagos, ObtenerDetallesOrden, obtenerTipoCambioActual]);

    /**
     * Guarda los cambios en el backend
     */
    const guardarCambiosPrecios = useCallback(async () => {
        setGuardandoCambios(true);

        try {
            const productosModificados = Object.keys(editedProducts).map(idOrdenDetalle => {
                const producto = editedProducts[idOrdenDetalle];
                return {
                    idOrdenDetalle: parseInt(idOrdenDetalle),
                    dCantidad: producto.dCantidad,
                    dPrecioUnitario: (parseFloat(producto.dPrecioUnitario) / 1.16).toFixed(6),
                    dDescuento: producto.dDescuento.toFixed(6),
                    dImporte: producto.dImporte.toFixed(6),
                    dIVA: producto.dIVA.toFixed(6),
                    dTotal: (producto.dImporte + producto.dIVA).toFixed(6)
                };
            });

            // Usar el método unificado que maneja moneda + totales
            const resultado = await actualizarMonedaYTotales(editedMoneda, productosModificados);

            if (resultado.success) {
                // Registrar comentario de los cambios realizados
                GuardarComentario(
                    orden.idOrden,
                    `<b>${nombreCompleto}</b> actualizó <b class='badge badge-soft-info'>precios y cantidades</b> de la orden. Moneda: <b>${editedMoneda}</b>${resultado.tipoCambio !== 1 ? ` (T.C: ${resultado.tipoCambio})` : ''}`,
                    2,
                    orden,
                    (comentarioBD) => {
                        onComentarioAgregado(comentarioBD);
                    },
                    (error) => {
                        showErrorToast(error);
                        
                    }
                );

                showSuccessToast('Precios y cantidades actualizados exitosamente');
                

                // Salir del modo edición
                setShowEditarPrecios(false);
                setEditedProducts({});
                setEditedMoneda('');
            }
        } catch (error) {
            showErrorToast('Error al guardar cambios: ' + error.message);
            
        } finally {
            setGuardandoCambios(false);
        }
    }, [editedProducts, editedMoneda, nombreCompleto, actualizarMonedaYTotales, GuardarComentario, onComentarioAgregado]);

    return {
        showEditarPrecios,
        editedProducts,
        editedMoneda,
        guardandoCambios,
        setEditedMoneda,
        inicializarEdicion,
        handleProductChange,
        cancelarEdicion,
        guardarCambiosPrecios
    };
};

export default useProductosEdicion;
